// src/pages/ForgotPasswordPage.jsx
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import BookyaraLogo from "../components/ui/BookyaraLogo";

export default function ForgotPasswordPage() {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [email,   setEmail]   = useState("");
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email) { setError("Please enter your email address."); return; }
    setLoading(true);
    try {
      await resetPassword(email);
      setSent(true);
    } catch (err) {
      setError("Could not send reset email. Check the address and try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-surface-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-brand-100 rounded-full blur-3xl opacity-60" />
      </div>

      <div className="relative w-full max-w-md animate-scale-in">
        <div className="text-center mb-8">
          <button onClick={() => navigate("/")} className="inline-flex flex-col items-center group">
            <BookyaraLogo size="md" className="mb-4" />
          </button>
        </div>

        <div className="auth-card">
          {sent ? (
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-brand-100 flex items-center justify-center mx-auto mb-4">
                <Mail size={24} className="text-brand-600" />
              </div>
              <h2 className="font-semibold text-xl text-ink-900 mb-2">Check your inbox</h2>
              <p className="text-ink-400 text-sm mb-6">
                We've sent a password reset link to <strong className="text-ink-700">{email}</strong>
              </p>
              <Link to="/login" className="btn-primary inline-flex items-center gap-2">
                <ArrowLeft size={16} /> Back to Sign In
              </Link>
            </div>
          ) : (
            <>
              <h2 className="font-semibold text-xl text-ink-900 mb-1">Reset your password</h2>
              <p className="text-ink-400 text-sm mb-6">Enter your email and we'll send you a reset link.</p>

              {error && <div className="alert-error mb-4">{error}</div>}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="form-label">Email Address</label>
                  <input type="email" placeholder="you@email.com"
                    value={email} onChange={e => { setEmail(e.target.value); setError(""); }}
                    className="form-input" />
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
                  {loading
                    ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    : <><Mail size={16} /> Send Reset Link</>
                  }
                </button>
              </form>

              <div className="divider my-6" />
              <Link to="/login" className="flex items-center justify-center gap-2 text-sm text-ink-400 hover:text-brand-600 transition-colors">
                <ArrowLeft size={15} /> Back to Sign In
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
